import React, { useState, useEffect } from "react";
import { useOrderChangesDialog } from "../../Components/orders/useOrderChangesDialog";
import OrderChangeImagesButton from "../../Components/orders/OrderChangeImagesButton";
import { useQuery } from "@tanstack/react-query";
import {
  checkDuplicatePhoneCount,
  fetchConfirmedOrders,
  updateOrderStatus,
  updateOrderStatusBulk,
  deleteOrder,
  fetchConfirmedOrdersSummary,
} from "../../api/OrderAPIs";
import { fetchDesignById } from "../../api/DesignAPIs";
import { fetchTypeById } from "../../api/TypeAPIs";
import { readyOrderUnits,formatPhoneWithCaseStyle,
  caseStyleNameFromDetail,
  teePlusPackedItemData,
  orderMatchesCaseStyleFilter,
  CASE_STYLE_FILTER_ALL,
  detailHasPersonalizedName,
  namedOrderRowClass,
  namedItemBoxClass,
} from "../../utils/orderLineDisplay";
import { NamedPieceBadge, OrderNameMark } from "../../Components/orders/NamedPieceBadge";
import CaseStyleOrderFilter from "../../Components/orders/CaseStyleOrderFilter";
import { useAuth } from "../../utils/auth";
import { canViewOrders, canMutateOrders, isAdmin } from "../../utils/roles";
import { useNavigate } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { API_BASE_URL_V2 } from "../../api/URL_Header";
import { downloadCustomFile } from "../../api/FileAPIs";
import ViewConversationButton from "../../Components/orders/ViewConversationButton";
import { copyTextToClipboard } from "../../utils/productionSource";
import {
  ChevronRight,
  ChevronLeft,
  ChevronUp,
  Package,
  User,
  Phone,
  MapPin,
  Map,
  DollarSign,
  Hash,
  Copy,
  Download,
  Link as LinkIcon,
  Edit,
  Trash2,
  Archive,
  Clock,
  AlertTriangle,
  ShoppingCart,
  Truck,
  CopyCheck,
  Filter,
  Save,
  Plus,
  Minus,
  Search,
  Loader,
  TrendingUp,
  Globe,
  Printer,
  CheckSquare,
  Send,
  Shirt,
} from "lucide-react";

const SkeletonLoader = ({ type }) => {
  if (type === "table") {
    return (
      <div className="animate-pulse">
        <div className="h-8 bg-gray-200 rounded mb-4"></div>
        {[...Array(3)].map((_, index) => (
          <div key={index} className="flex gap-4 border-b py-3">
            <div className="w-12 h-12 bg-gray-200 rounded"></div>
            <div className="flex-1 space-y-2">
              <div className="h-4 bg-gray-200 rounded w-3/4"></div>
              <div className="h-4 bg-gray-200 rounded w-1/2"></div>
            </div>
          </div>
        ))}
      </div>
    );
  } else if (type === "card") {
    return (
      <div className="animate-pulse bg-white/80 backdrop-blur-sm rounded-2xl p-6">
        <div className="h-6 bg-gray-200 rounded w-1/2 mb-2"></div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="h-6 bg-gray-200 rounded w-3/4"></div>
          <div className="h-6 bg-gray-200 rounded w-3/4"></div>
        </div>
      </div>
    );
  } else if (type === "productsCard") {
    return (
      <div className="animate-pulse bg-white/80 backdrop-blur-sm rounded-2xl p-6">
        <div className="h-6 bg-gray-200 rounded w-1/2 mb-2"></div>
        <div className="h-6 bg-gray-200 rounded w-3/4"></div>
      </div>
    );
  }
  return null;
};

const CONFIRMED_FROM_SECOND_PHONE_NOTE = "تم التأكيد مو الرقم التاني";
const CONFIRMED_FROM_SECOND_PHONE_LABEL = "تم التأكيد من الرقم التاني";

const isConfirmedFromSecondPhone = (note) =>
  note && note.includes(CONFIRMED_FROM_SECOND_PHONE_NOTE);

const buildFirstItemLine = (customerBase, itemLine, note) =>
  isConfirmedFromSecondPhone(note)
    ? `${customerBase}${CONFIRMED_FROM_SECOND_PHONE_LABEL}\t\t${itemLine}`
    : `${customerBase}\t\t${itemLine}`;

const ConfirmedOrdersPage = () => {
  const navigate = useNavigate();
  const { authState } = useAuth();
  const canAct = canMutateOrders(authState.role);
  const { changesDialog, openOrderChanges } = useOrderChangesDialog(async (savedOrderId) => {
    setLoadedOrders((prev) => prev.filter((o) => o.orderNumber !== savedOrderId));
    toast.success("تم تحديث الطلب إلى Changes بنجاح");
  });
  
  const [loadedOrders, setLoadedOrders] = useState([]);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [isOrdersLoading, setIsOrdersLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [error, setError] = useState(null);
  const [currentReference, setCurrentReference] = useState("Girabatcom.16000");
  const [referenceInput, setReferenceInput] = useState("Girabatcom.16000");
  const [showFilters, setShowFilters] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [caseStyleFilter, setCaseStyleFilter] = useState(CASE_STYLE_FILTER_ALL);
  const [pageSize, setPageSize] = useState(10);
  const [selectedOrderNumbers, setSelectedOrderNumbers] = useState(new Set());
  const [isBulkUpdating, setIsBulkUpdating] = useState(false);

  const { data: confirmedOrdersSummary, isLoading: isLoadingSummary, error: errorSummary } = useQuery({
    queryKey: ["confirmedOrdersSummary"],
    queryFn: fetchConfirmedOrdersSummary,
    enabled: isAdmin(authState.role),
    staleTime: Infinity,
    placeholderData: { count: 0, totalProfit: 0, totalAmount: 0 },
  });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (!canViewOrders(authState.role)) {
      navigate("/login");
      return;
    }
    loadInitialOrders();
  }, [authState, navigate]);

  useEffect(() => {
    const saved = localStorage.getItem("orderReference");
    if (saved) {
      setCurrentReference(saved);
      setReferenceInput(saved);
    }
  }, []);

  const isLoading = isLoadingSummary;
  const queryError = errorSummary;

  const handleDownload = async (url, filename = "design.jpg") => {
    try {
      await downloadCustomFile(url, filename);
    } catch (err) {
      console.error("Download failed:", err);
      toast.error("فشل التحميل. تحقق من السيرفر.");
    }
  };

  const copyToClipboard = (text) => {
    if (navigator.clipboard && window.isSecureContext) {
      return navigator.clipboard.writeText(text).catch(() => {
        fallbackCopy(text);
      });
    } else {
      fallbackCopy(text);
    }
  };

  const handleCopyPhoneNumber = (phoneNumber) => {
    copyToClipboard(phoneNumber);
    toast.success(`تم نسخ الرقم: ${phoneNumber}`);
  };

  const fallbackCopy = (text) => {
    const textArea = document.createElement("textarea");
    textArea.value = text;
    textArea.style.position = "fixed";
    textArea.style.opacity = "0";
    textArea.style.left = "-999999px";
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();

    try {
      const successful = document.execCommand("copy");
      if (!successful) {
        toast.error("فشل النسخ، حاول مرة أخرى.");
      }
    } catch (err) {
      toast.error("فشل النسخ، حاول النسخ يدويًا.");
      console.error("Copy failed:", err);
    }

    document.body.removeChild(textArea);
  };

  const enrichOrders = async (data) => {
    return await Promise.all(
      data.map(async (order) => {
        let enrichedItems = [];

        for (const detail of order.orderDetails) {
          const readyUnits = await readyOrderUnits(detail);
          if (readyUnits) {
            enrichedItems.push(...readyUnits);
            continue;
          }
          const design = await fetchDesignById(detail.designId);
          const type = await fetchTypeById(detail.typeId);
          if (!design || !type) continue;

          const baseItem = {
            id: detail.designId,
            name: design.Name,
            price: detail.price,
            phoneType: type.name || "",
            caseStyleName: caseStyleNameFromDetail(detail),
            type: formatPhoneWithCaseStyle(type.name, detail),
            driveLink: design.code.startsWith("CUSTOM") ? `${API_BASE_URL_V2}${design.driveLink}` : design.driveLink || "",
            code: design.code || "None",
            customNameAr: detail.customNameAr || detail.CustomNameAr || "",
            customNameEn: detail.customNameEn || detail.CustomNameEn || "",
            hasPersonalizedName: detailHasPersonalizedName(detail),
          };

          for (let i = 0; i < detail.quantity; i++) {
            enrichedItems.push({
              ...baseItem,
              quantityIndex: i + 1,
              totalQuantity: detail.quantity,
            });
          }
        }

        return {
          orderNumber: order.orderId,
          items: enrichedItems,
          total: order.totalAmount,
          profit: order.profit || 0,
          date: new Date(order.date).toISOString().split("T")[0],
          address: order.address?.fullAddress || "Unknown Address",
          state: order.address?.state || "Unknown Address",
          phoneNumber: order.phoneNumber || "Unknown Phone",
          phoneNumber2: order.phoneNumber2 || "Unknown Phone",
          name: order.name || "Unknown Customer",
          note: order.note || "",
          changeNoteImageCount: Number(order.changeNoteImageCount || 0),
          status: order.status || "Confirmed",
        };
      })
    );
  };

  const loadInitialOrders = async () => {
    setIsOrdersLoading(true);
    try {
      const data = await fetchConfirmedOrders(0, pageSize);
      if (data.length === 0 || data.length < pageSize) {
        setHasMore(false);
      }
      const enrichedOrders = await enrichOrders(data);
      setLoadedOrders(enrichedOrders);
      setCurrentPage(1);
    } catch (err) {
      console.error("Error loading initial orders:", err);
      setError("فشل في تحميل الطلبات. يرجى المحاولة مرة أخرى.");
      toast.error("فشل في تحميل الطلبات!");
    } finally {
      setIsOrdersLoading(false);
    }
  };

  const loadMoreOrders = async () => {
    setIsLoadingMore(true);
    try {
      const data = await fetchConfirmedOrders(currentPage, pageSize);
      if (data.length === 0 || data.length < pageSize) {
        setHasMore(false);
      }
      const enrichedOrders = await enrichOrders(data);
      setLoadedOrders((prev) => [...prev, ...enrichedOrders]);
      setCurrentPage((prev) => prev + 1);
    } catch (err) {
      console.error("Error loading more orders:", err);
      setError("فشل في تحميل المزيد من الطلبات. يرجى المحاولة مرة أخرى.");
      toast.error("فشل في تحميل المزيد من الطلبات!");
    } finally {
      setIsLoadingMore(false);
    }
  };

  const handlePageSizeChange = async (newSize) => {
    setPageSize(newSize);
    setCurrentPage(0);
    setIsOrdersLoading(true);
    try {
      const data = await fetchConfirmedOrders(0, newSize);
      const enrichedOrders = await enrichOrders(data);
      setLoadedOrders(enrichedOrders);
      setSelectedOrderNumbers(new Set());
      setCurrentPage(1);
      setHasMore(data.length === newSize);
    } catch (err) {
      console.error("Error changing page size:", err);
      toast.error("فشل في تحميل الطلبات!");
    } finally {
      setIsOrdersLoading(false);
    }
  };

  const handleUpdateStatus = async (orderId, newStatus) => {
    if (!canAct) return;
    let reason = "no reason (normal order)";

    if (newStatus === "Old") {
      try {
        const confirmUpdate = window.confirm("هل تريد تحديث الطلب إلى قديم؟");
        if (!confirmUpdate) return;

        await updateOrderStatus(orderId, newStatus, reason);
        setLoadedOrders((prev) => prev.filter((o) => o.orderNumber !== orderId));
        toast.success("تم تحديث الطلب إلى قديم!");
      } catch (err) {
        console.error(err);
        toast.error("فشل في العملية: " + (err.message || "خطأ غير معروف"));
      }
      return;
    }

    if (newStatus === "Hold") {
      reason = window.prompt("سبب وضع الطلب على الانتظار:");
      if (reason === null) return;
      if (!reason.trim()) {
        toast.error("يجب إدخال سبب!");
        return;
      }
    } else if (newStatus === "Changes") {
      openOrderChanges(orderId);
      return;
    } else if (newStatus === "Spam") {
      reason = window.prompt("سبب التغيير:");
      if (reason === null) return;
      if (!reason.trim()) {
        toast.error("يجب إدخال سبب!");
        return;
      }
    } else if (newStatus === "Incomplete") {
      const note = window.prompt(
        "ملاحظة Incomplete (اختياري) — السبب أو نوع الجراب الناقص:\nاتركه فاضي لو مش عايز تكتب حاجة"
      );
      if (note === null) return;
      reason = note.trim() || "no reason (normal order)";
    }

    const confirm = window.confirm(`تأكيد تحديث الحالة إلى "${newStatus}"؟`);
    if (!confirm) return;

    try {
      await updateOrderStatus(orderId, newStatus, reason);
      setLoadedOrders((prev) => prev.filter((o) => o.orderNumber !== orderId));
      toast.success(`تم تحديث الطلب إلى ${newStatus} بنجاح`);
    } catch (err) {
      toast.error("فشل التحديث: " + err.message);
    }
  };

  const handleDeleteOrder = async (orderId) => {
    if (!canAct) return;
    if (!canMutateOrders(authState.role)) return;
    const confirmDelete = window.confirm(`هل أنت متأكد من حذف الطلب رقم ${orderId}؟`);
    if (!confirmDelete) return;
    try {
      await deleteOrder(orderId);
      setLoadedOrders((prev) => prev.filter((order) => order.orderNumber !== orderId));
      toast.success(`تم حذف الطلب رقم ${orderId} بنجاح!`);
    } catch (err) {
      console.error("Error deleting order:", err);
      toast.error("فشل في حذف الطلب. يرجى المحاولة مرة أخرى.");
    }
  };

  const handleCopyBostaMessage = () => {
    const message = `
بنحب نبلغ حضرتك إن شركة الشحن هتتواصل مع حضرتك على الواتساب خلال وقت قصير لتأكيد العنوان واستلام الطلب ✅
تنويه مهم:
في رسالة التأكيد اللي هتوصلك ممكن تلاقي اسم شركة مختلفة (مثل TeePlus) 💼  

كمان عدد القطع ممكن يظهر 1 حتى لو كانت اكتر من قطعة واحدة 🧾
يُرجى الرد على رسالة شركة الشحن علشان يتم تأكيد الطلب ويتم بدء الشحن ✉️⚡  

شكرًا لاختيارك جرابتكم 🌷`;
    copyToClipboard(message);
  };

  const handleCopyAddressClarification = () => {
    const message = `أهلاً بحضرتك

العنوان المسجل غير واضح أو ناقص تفاصيل مهمة لتسليم الطلب بنجاح

برجاء توضيح العنوان بالتفصيل:
• اسم الشارع
• رقم العمارة / الشقة
• معالم قريبة (مثل: بجوار صيدلية، أمام مدرسة، إلخ)

شكرًا لتفهمك، هنبدأ تجهيز الطلب فور توضيح العنوان`;

    copyToClipboard(message);
  };

  const handleCopyAllExcelData = async (order) => {
    try {
      const count = await checkDuplicatePhoneCount(
        order.orderNumber,
        order.phoneNumber,
        order.phoneNumber2 || ""
      );

      if (count > 0) {
        const proceed = window.confirm(
          `تحذير: هذا العميل لديه ${count} طلب(ات) سابقة بنفس الرقم!\n\nهل تريد الاستمرار في النسخ؟`
        );
        if (!proceed) {
          toast.info("تم إلغاء النسخ بسبب تكرار الرقم");
          return;
        }
      }

      const phone = `${order.phoneNumber}${
        order.phoneNumber2 && order.phoneNumber2 !== "Unknown Phone" ? "-" + order.phoneNumber2 : ""
      }`;
      const formattedState = `${order.state} (D)`;
      const customerBase = `${order.name}\t${phone}\t\t${order.address}\t${formattedState}\t${order.total}\tCOD\t`;

      const items = order.items;
      const lines = [];

      items.forEach((item, index) => {
        const itemNum = `${index + 1}-${items.length}`;
        const itemData = teePlusPackedItemData(item);
        const itemLine = `${itemNum}${itemData}`;

        if (index === 0) {
          lines.push(buildFirstItemLine(customerBase, itemLine, order.note));
        } else {
          lines.push(`\t\t\t\t\t\t\t\t\t${itemLine}`);
        }
      });

      const finalText = lines.join("\n");
      const copied = await copyTextToClipboard(finalText);
      if (!copied) throw new Error("COPY_FAILED");

      toast.success("تم نسخ الطلب كامل بدون مرجع بنجاح!");
    } catch (err) {
      console.error("خطأ في النسخ:", err);
      toast.error("فشل في النسخ، حاول مرة تانية");
    }
  };

  const isOrderNonCustom = (order) => {
    return !order.items.some((item) => item.code && item.code.startsWith("CUSTOM"));
  };

  const toggleSelectOrder = (orderNumber) => {
    setSelectedOrderNumbers((prev) => {
      const next = new Set(prev);
      if (next.has(orderNumber)) next.delete(orderNumber);
      else next.add(orderNumber);
      return next;
    });
  };

  const setSelectAllVisible = (checked, visibleOrders) => {
    setSelectedOrderNumbers((prev) => {
      const next = new Set(prev);
      if (checked) visibleOrders.forEach((o) => next.add(o.orderNumber));
      else visibleOrders.forEach((o) => next.delete(o.orderNumber));
      return next;
    });
  };

  const selectedOrders = () =>
    loadedOrders.filter((o) => selectedOrderNumbers.has(o.orderNumber));

  const copyOrdersByReference = async (orders) => {
    const nonCustomOrders = orders.filter(isOrderNonCustom);

    if (nonCustomOrders.length === 0) {
      toast.warn("لا توجد طلبات غير مخصصة (CUSTOM) للنسخ");
      return;
    }

    const confirmCopy = window.confirm(
      `هل تريد نسخ ${nonCustomOrders.length} طلب(ات) غير مخصصة مرة واحدة إلى الإكسيل؟\n\n(الطلبات التي تحتوي على تصميم CUSTOM سيتم تخطيها)\n\nسيتم فحص التكرار + زيادة الرقم المرجع تلقائيًا`
    );
    if (!confirmCopy) return;

    let finalLines = [];
    let hasDuplicate = false;
    let currentRef = currentReference;

    for (const order of nonCustomOrders) {
      try {
        const count = await checkDuplicatePhoneCount(
          order.orderNumber,
          order.phoneNumber,
          order.phoneNumber2 || ""
        );

        if (count > 0) {
          hasDuplicate = true;
          finalLines.push(`# تحذير: تكرار رقم (${count} طلب سابق) - ${order.name} - ${order.phoneNumber}`);
        }

        const phone = `${order.phoneNumber}${
          order.phoneNumber2 && order.phoneNumber2 !== "Unknown Phone" ? "-" + order.phoneNumber2 : ""
        }`;
        const formattedState = `${order.state} (D)`;
        const customerBase = `${currentRef}\t\t${order.name}\t${phone}\t\t${order.address}\t${formattedState}\t${order.total}\tCOD\t`;

        const items = order.items;
        const orderLines = [];

        items.forEach((item, index) => {
          const itemNum = `${index + 1}-${items.length}`;
          const itemData = teePlusPackedItemData(item);
          const itemLine = `${itemNum}${itemData}`;

          if (index === 0) {
            orderLines.push(buildFirstItemLine(customerBase, itemLine, order.note));
          } else {
            orderLines.push(`${currentRef}\t\t\t\t\t\t\t\t\t\t\t${itemLine}`);
          }
        });

        finalLines.push(...orderLines);

        const match = currentRef.match(/(\d+)$/);
        if (match) {
          const num = parseInt(match[0], 10);
          currentRef = currentRef.replace(/\d+$/, (num + 1).toString());
        }
      } catch (err) {
        finalLines.push(`# خطأ في الطلب رقم ${order.orderNumber}`);
      }
    }

    setCurrentReference(currentRef);
    setReferenceInput(currentRef);
    localStorage.setItem("orderReference", currentRef);

    const finalText = finalLines.join("\n");
    try {
      const copied = await copyTextToClipboard(finalText);
      if (!copied) throw new Error("COPY_FAILED");
      toast.success(
        `تم نسخ ${nonCustomOrders.length} طلب(ات) غير مخصصة بنجاح!${hasDuplicate ? " (يوجد تكرار في بعض الأرقام)" : ""}`,
        { autoClose: 6000 }
      );
    } catch (err) {
      toast.error("فشل في النسخ، حاول مرة تانية");
    }
  };

  const handleCopyAllVisibleOrders = async () => {
    await copyOrdersByReference(loadedOrders);
  };

  const handleCopySelectedOrders = async () => {
    const selected = selectedOrders();
    if (selected.length === 0) {
      toast.info("اختر أوردرات أولاً");
      return;
    }
    await copyOrdersByReference(selected);
  };

  const applyBulkStatus = async (status, reason, confirmMessage) => {
    if (!canAct) return;
    let selected = selectedOrders();
    if (selected.length === 0) {
      toast.info("اختر أوردرات أولاً");
      return;
    }
    if (!window.confirm(confirmMessage.replace("{count}", String(selected.length)))) return;

    setIsBulkUpdating(true);
    try {
      const ids = selected.map((o) => o.orderNumber);
      const result = await updateOrderStatusBulk(ids, status, reason);
      const okIds = new Set(
        (result.results || [])
          .filter((r) => r.ok)
          .map((r) => r.orderId)
      );
      const failed = (result.results || []).filter((r) => !r.ok);
      setLoadedOrders((prev) => prev.filter((o) => !okIds.has(o.orderNumber)));
      setSelectedOrderNumbers((prev) => {
        const next = new Set(prev);
        okIds.forEach((id) => next.delete(id));
        return next;
      });
      toast.success(`تم تحديث ${okIds.size} طلب(ات) إلى ${status}`);
      failed.slice(0, 5).forEach((r) =>
        toast.error(`#${r.orderId}: ${typeof r.error === "string" ? r.error : "فشل التحديث"}`)
      );
    } catch (err) {
      toast.error(err.message || "فشل التحديث الجماعي");
    } finally {
      setIsBulkUpdating(false);
    }
  };

  const handleSendSelectedToPrintlet = () =>
    applyBulkStatus(
      "Printlet",
      "نقل لطابور Printlet",
      `تحويل {count} طلب(ات) محددة إلى حالة Printlet؟\n\nلن يتم إرسالها إلى Printlet API الآن — هتظهر في صفحة طلبات Printlet.`
    );

  const handleSendSelectedToOld = () =>
    applyBulkStatus(
      "Old",
      "تم التحديث الجماعي للمحدد",
      `هل تريد تحويل {count} طلب(ات) محددة إلى "قديم"؟\n\nالطلبات المحددة ستختفي من الصفحة فورًا.`
    );

  const handleSendSelectedToPrintingHold = () =>
    applyBulkStatus(
      "Printing Hold",
      "تم التحويل الجماعي إلى Printing Hold",
      `تحويل {count} طلب(ات) محددة إلى Printing Hold؟\n\nالطلبات المحددة ستختفي من الصفحة فورًا.`
    );

  const handleSendSelectedToTeePlus = () =>
    applyBulkStatus(
      "TeePlus",
      "نقل لطابور Tee Plus",
      `تحويل {count} طلب(ات) محددة إلى حالة Tee Plus؟\n\nهتظهر في صفحة طلبات Tee Plus، ومصدر الإنتاج هيبقى TeePlus تلقائيًا.`
    );

  const handleMarkAllAsOld = async () => {
    const nonCustomOrders = loadedOrders.filter(isOrderNonCustom);

    if (nonCustomOrders.length === 0) {
      toast.warn("لا توجد طلبات غير مخصصة (CUSTOM) لتحويلها إلى قديم");
      return;
    }

    const confirm = window.confirm(
      `هل تريد تحويل ${nonCustomOrders.length} طلب(ات) غير مخصصة إلى "قديم"؟\n\n` +
        `(الطلبات التي تحتوي على تصميم CUSTOM سيتم تخطيها)\n\n` +
        `الطلبات المحددة ستختفي من الصفحة فورًا.`
    );

    if (!confirm) return;

    let successCount = 0;
    let failedCount = 0;

    toast.info("جاري تحديث الطلبات غير المخصصة إلى قديم... لا تغلق الصفحة", {
      autoClose: false,
      toastId: "marking-old",
    });

    for (const order of nonCustomOrders) {
      try {
        await updateOrderStatus(order.orderNumber, "Old", "تم التحديث الجماعي بدون نسخ (غير مخصص)");
        successCount++;
      } catch (err) {
        console.error(`فشل تحديث الطلب ${order.orderNumber}:`, err);
        failedCount++;
      }
    }

    setLoadedOrders((prev) => prev.filter((order) => !nonCustomOrders.some((o) => o.orderNumber === order.orderNumber)));

    toast.dismiss("marking-old");

    toast.success(
      `تم تحويل ${successCount} طلب(ات) غير مخصصة إلى قديم بنجاح!` +
        (failedCount > 0 ? `\nفشل في ${failedCount} طلب(ات)` : ""),
      { autoClose: 6000 }
    );
  };

  const filteredOrders = loadedOrders.filter((order) => {
    if (!orderMatchesCaseStyleFilter(order, caseStyleFilter)) return false;
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (
      order.orderNumber.toString().includes(term) ||
      order.name.toLowerCase().includes(term) ||
      order.phoneNumber.includes(term) ||
      order.phoneNumber2?.includes(term)
    );
  });

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50 p-4 md:p-8" dir="rtl">
      {changesDialog}
      <ToastContainer
        position="bottom-left"
        autoClose={2500}
        hideProgressBar
        newestOnTop
        closeOnClick
        pauseOnHover={false}
        rtl
        limit={3}
        className="compact-toasts"
      />

      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-center gap-4 mb-8">
          <div className="flex items-center gap-3">
            <Package className="text-indigo-600" size={32} />
            <h1 className="text-3xl md:text-4xl font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">
              الطلبات المؤكدة
            </h1>
          </div>
          <button
            onClick={() => navigate("/dashboard")}
            className="flex items-center gap-2 bg-white/80 backdrop-blur-sm text-gray-700 px-6 py-3 rounded-xl shadow-md hover:shadow-xl transition-all duration-300 border border-gray-200"
          >
            <ChevronRight size={20} />
            العودة للرئيسية
          </button>
        </div>

        {(error || queryError) && (
          <div className="bg-red-100 text-red-700 p-4 rounded-xl mb-6 border border-red-200">
            {error || "فشل في تحميل بيانات الطلبات المؤكدة. يرجى المحاولة مرة أخرى."}
          </div>
        )}

        {/* Stats Cards */}
        {isAdmin(authState.role) && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
            <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl p-6 border border-white/30 hover:shadow-2xl transition-all">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-3 bg-indigo-100 rounded-xl">
                  <Package className="text-indigo-600" size={24} />
                </div>
                <h2 className="text-xl font-bold text-gray-800">ملخص الطلبات المؤكدة</h2>
              </div>
              {isLoading ? (
                <SkeletonLoader type="card" />
              ) : (
                <div className="space-y-3">
                  <div className="flex justify-between items-center p-3 bg-indigo-50 rounded-xl">
                    <span className="text-gray-600">عدد الطلبات</span>
                    <span className="text-2xl font-bold text-indigo-600">{confirmedOrdersSummary.count}</span>
                  </div>
                  <div className="flex justify-between items-center p-3 bg-green-50 rounded-xl">
                    <span className="text-gray-600">إجمالي المبلغ</span>
                    <span className="text-2xl font-bold text-green-600">{confirmedOrdersSummary.totalAmount} ج.م</span>
                  </div>
                </div>
              )}
            </div>

            <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl p-6 border border-white/30 hover:shadow-2xl transition-all">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-3 bg-purple-100 rounded-xl">
                  <TrendingUp className="text-purple-600" size={24} />
                </div>
                <h2 className="text-xl font-bold text-gray-800">إجمالي الأرباح</h2>
              </div>
              {isLoading ? (
                <SkeletonLoader type="productsCard" />
              ) : (
                <div className="space-y-3">
                  <div className="flex justify-between items-center p-3 bg-purple-50 rounded-xl">
                    <span className="text-gray-600">الربح الإجمالي</span>
                    <span className="text-2xl font-bold text-purple-600">{confirmedOrdersSummary.totalProfit} ج.م</span>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Quick Message Buttons */}
        {/* <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
          <div className="bg-gradient-to-r from-amber-50 to-yellow-50 backdrop-blur-sm rounded-2xl shadow-xl p-6 border border-amber-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-3 bg-amber-100 rounded-xl">
                  <MapPin className="text-amber-600" size={24} />
                </div>
                <div>
                  <h3 className="font-bold text-amber-900 text-lg">طلب توضيح العنوان</h3>
                  <p className="text-sm text-amber-700">إذا كان العنوان غير واضح</p>
                </div>
              </div>
              <button
                onClick={handleCopyAddressClarification}
                className="bg-amber-600 hover:bg-amber-700 text-white px-6 py-3 rounded-xl shadow-md hover:shadow-xl transition-all duration-300 flex items-center gap-2"
              >
                <Copy size={18} />
                نسخ الرسالة
              </button>
            </div>
          </div>

          <div className="bg-gradient-to-r from-blue-50 to-cyan-50 backdrop-blur-sm rounded-2xl shadow-xl p-6 border border-blue-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-3 bg-blue-100 rounded-xl">
                  <Truck className="text-blue-600" size={24} />
                </div>
                <div>
                  <h3 className="font-bold text-blue-900 text-lg">رسالة بوسطة</h3>
                  <p className="text-sm text-blue-700">لشركة الشحن</p>
                </div>
              </div>
              <button
                onClick={handleCopyBostaMessage}
                className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-xl shadow-md hover:shadow-xl transition-all duration-300 flex items-center gap-2"
              >
                <Copy size={18} />
                نسخ الرسالة
              </button>
            </div>
          </div>
        </div> */}

        {/* Bulk Actions */}
        {/* {isAdmin(authState.role) && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
            <div className="bg-gradient-to-r from-green-50 to-emerald-50 backdrop-blur-sm rounded-2xl shadow-xl p-6 border-2 border-green-300">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-3 bg-green-100 rounded-xl">
                    <CopyCheck className="text-green-600" size={24} />
                  </div>
                  <div>
                    <h3 className="font-bold text-green-900 text-xl">نسخ جماعي للإكسيل</h3>
                    <p className="text-sm text-green-700">نسخ المحدد أو كل الظاهر مع فحص التكرار</p>
                  </div>
                </div>
                <div className="flex flex-col gap-2">
                  <button
                    onClick={handleCopySelectedOrders}
                    disabled={isOrdersLoading || isBulkUpdating || selectedOrderNumbers.size === 0}
                    className="bg-green-600 hover:bg-green-700 disabled:bg-gray-400 text-white font-bold px-6 py-3 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 disabled:cursor-not-allowed flex items-center gap-2"
                  >
                    <Copy size={20} />
                    نسخ المحدد (TeePlus) ({selectedOrderNumbers.size})
                  </button>
                  <button
                    onClick={handleCopyAllVisibleOrders}
                    disabled={isOrdersLoading || loadedOrders.length === 0 || isBulkUpdating}
                    className="bg-white border-2 border-green-500 text-green-800 font-bold px-6 py-3 rounded-xl hover:bg-green-50 disabled:opacity-50 transition-all flex items-center gap-2"
                  >
                    <Copy size={18} />
                    نسخ الكل الظاهر (TeePlus)
                  </button>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-r from-purple-50 to-indigo-50 backdrop-blur-sm rounded-2xl shadow-xl p-6 border-2 border-purple-300">
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                  <div className="p-3 bg-purple-100 rounded-xl">
                    <Archive className="text-purple-600" size={24} />
                  </div>
                  <div>
                    <h3 className="font-bold text-purple-900 text-xl">تحويل المحدد</h3>
                    <p className="text-sm text-purple-700">المحدد: {selectedOrderNumbers.size} طلب</p>
                  </div>
                </div>
                <div className="flex flex-col gap-2">
                  <button
                    onClick={handleSendSelectedToPrintlet}
                    disabled={isOrdersLoading || isBulkUpdating || selectedOrderNumbers.size === 0}
                    className="bg-fuchsia-600 hover:bg-fuchsia-700 disabled:bg-gray-400 text-white font-bold px-6 py-3 rounded-xl shadow-lg disabled:cursor-not-allowed flex items-center gap-2"
                  >
                    <Send size={18} />
                    تحويل لـ Printlet
                  </button>
                  <button
                    onClick={handleSendSelectedToOld}
                    disabled={isOrdersLoading || isBulkUpdating || selectedOrderNumbers.size === 0}
                    className="bg-purple-600 hover:bg-purple-700 disabled:bg-gray-400 text-white font-bold px-6 py-3 rounded-xl shadow-lg disabled:cursor-not-allowed flex items-center gap-2"
                  >
                    <Archive size={18} />
                    تحويل للقديم
                  </button>
                </div>
              </div>
            </div>
          </div>
        )} */}

        {/* Reference Section */}
        {isAdmin(authState.role) && (
        <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl p-6 mb-8 border border-white/30">
          <div className="flex items-center gap-3 mb-4">
            <Hash className="text-indigo-600" size={24} />
            <h2 className="text-xl font-bold text-gray-800">إعداد رقم المرجع (Reference)</h2>
          </div>
          <div className="flex flex-col gap-4">
            <div className="flex flex-col md:flex-row items-center gap-4">
            <div className="flex-1 w-full">
              <label className="block text-sm font-medium text-gray-700 mb-1 text-right">
                ابدأ من (مثال: Girabatcom.150)
              </label>
              <input
                type="text"
                value={referenceInput}
                onChange={(e) => setReferenceInput(e.target.value)}
                onBlur={() => {
                  if (referenceInput.trim() && referenceInput.includes(".")) {
                    setCurrentReference(referenceInput.trim());
                    localStorage.setItem("orderReference", referenceInput.trim());
                    toast.success("تم تحديث رقم المرجع!");
                  }
                }}
                className="w-full p-4 text-lg border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-4 focus:ring-indigo-300 focus:border-indigo-400 transition-all"
                placeholder="Girabatcom.4000"
                style={{ fontFamily: "cairo black !important" }}
              />
            </div>
            <button
              onClick={() => {
                if (referenceInput.trim()) {
                  setCurrentReference(referenceInput.trim());
                  setReferenceInput(referenceInput.trim());
                  localStorage.setItem("orderReference", referenceInput.trim());
                  toast.success("تم تحديث رقم المرجع!");
                }
              }}
              className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white px-8 py-4 rounded-xl hover:shadow-xl hover:scale-105 transition-all duration-300 font-bold flex items-center gap-2"
            >
              <Save size={20} />
              تحديث الرقم
            </button>
            <button
              onClick={handleCopySelectedOrders}
              disabled={isOrdersLoading || isBulkUpdating || selectedOrderNumbers.size === 0}
              className="bg-green-600 hover:bg-green-700 disabled:bg-gray-400 text-white font-bold px-5 py-4 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 disabled:cursor-not-allowed flex items-center gap-2"
            >
              <Copy size={20} />
              نسخ المحدد (TeePlus) ({selectedOrderNumbers.size})
            </button>
            </div>

            <div className="flex flex-col md:flex-row items-center gap-4">
            <button
              onClick={handleSendSelectedToPrintlet}
              disabled={isOrdersLoading || isBulkUpdating || selectedOrderNumbers.size === 0}
              className="bg-fuchsia-600 hover:bg-fuchsia-700 disabled:bg-gray-400 text-white font-bold px-5 py-4 rounded-xl shadow-lg disabled:cursor-not-allowed flex items-center justify-center gap-2 w-full"
            >
              <Send size={20} />
              Printlet ({selectedOrderNumbers.size})
            </button>
            
            <button
              onClick={handleSendSelectedToPrintingHold}
              disabled={isOrdersLoading || isBulkUpdating || selectedOrderNumbers.size === 0}
              className="bg-slate-700 hover:bg-slate-800 disabled:bg-gray-400 text-white font-bold px-5 py-4 rounded-xl shadow-lg disabled:cursor-not-allowed flex items-center justify-center gap-2 w-full"
            >
              <Printer size={20} />
              Printing Hold ({selectedOrderNumbers.size})
            </button>

            <button
              onClick={handleSendSelectedToTeePlus}
              disabled={isOrdersLoading || isBulkUpdating || selectedOrderNumbers.size === 0}
              className="bg-emerald-600 hover:bg-emerald-700 disabled:bg-gray-400 text-white font-bold px-5 py-4 rounded-xl shadow-lg disabled:cursor-not-allowed flex items-center justify-center gap-2 w-full"
            >
              <Shirt size={20} />
              Tee Plus ({selectedOrderNumbers.size})
            </button>

            <button
              onClick={handleSendSelectedToOld}
              disabled={isOrdersLoading || isBulkUpdating || selectedOrderNumbers.size === 0}
              className="bg-purple-600 hover:bg-purple-700 disabled:bg-gray-400 text-white font-bold px-5 py-4 rounded-xl shadow-lg disabled:cursor-not-allowed flex items-center justify-center gap-2 w-full"
            >
              <Archive size={20} />
              قديم ({selectedOrderNumbers.size})
            </button>
            </div>
          </div>
          <p className="text-sm text-gray-500 mt-3 text-right">
            كل ما تنسخ بيانات عميل بعد "تحديث إلى قديم"، الرقم سيزيد تلقائيًا
          </p>
        </div>
        )}
        
        {/* Search and Filter Bar */}
        <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl p-4 mb-6 border border-white/30">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
              <input
                type="text"
                placeholder="بحث برقم الطلب أو اسم العميل أو رقم الهاتف..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pr-12 pl-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-4 focus:ring-indigo-300 focus:border-indigo-400 transition-all"
              />
            </div>
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="flex items-center gap-2 px-6 py-3 bg-white border-2 border-gray-200 rounded-xl hover:bg-gray-50 transition-all"
            >
              <Filter size={20} />
              تصفية
            </button>
          </div>
          <CaseStyleOrderFilter
            value={caseStyleFilter}
            onChange={setCaseStyleFilter}
            orders={loadedOrders}
            accent="indigo"
          />
        </div>

        {/* Page Size Selector */}
        <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl p-4 mb-6 border border-white/30">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <label className="font-medium text-gray-700">عرض:</label>
              <select
                value={pageSize}
                onChange={(e) => handlePageSizeChange(Number(e.target.value))}
                className="px-4 py-2 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-4 focus:ring-indigo-300 focus:border-indigo-400 transition-all bg-white"
                disabled={isOrdersLoading}
              >
                <option value={10}>10 طلبات</option>
                <option value={20}>20 طلب</option>
                <option value={30}>30 طلب</option>
                <option value={40}>40 طلب</option>
                <option value={50}>50 طلب</option>
                <option value={80}>80 طلب</option>
                <option value={100}>100 طلب</option>
              </select>
              <span className="text-2xl text-gray-600">(إجمالي المعروض: {loadedOrders.length})</span>
              {canAct && (
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setSelectAllVisible(true, filteredOrders)}
                    disabled={filteredOrders.length === 0 || isBulkUpdating}
                    className="bg-white border-2 border-indigo-300 text-indigo-800 font-bold px-4 py-2 rounded-xl hover:bg-indigo-50 disabled:opacity-50 flex items-center gap-2"
                  >
                    <CheckSquare size={16} />
                    تحديد الظاهر
                  </button>
                  <button
                    type="button"
                    onClick={() => setSelectAllVisible(false, filteredOrders)}
                    disabled={selectedOrderNumbers.size === 0 || isBulkUpdating}
                    className="bg-white border-2 border-gray-300 text-gray-700 font-bold px-4 py-2 rounded-xl hover:bg-gray-50 disabled:opacity-50"
                  >
                    إلغاء التحديد
                  </button>
                </div>
              )}
            </div>
            {isOrdersLoading && (
              <div className="flex items-center gap-2 text-indigo-600">
                <Loader className="animate-spin" size={20} />
                <span>جاري التحميل...</span>
              </div>
            )}
          </div>
        </div>

        {/* Orders Table */}
        <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl p-6 border border-white/30 overflow-x-auto">
          {isOrdersLoading ? (
            <SkeletonLoader type="table" />
          ) : (
            <>
              {filteredOrders.length > 0 ? (
                <>
                  <table className="w-full min-w-[1200px] table-auto">
                    <thead>
                      <tr className="bg-gradient-to-r from-indigo-50 to-purple-50 border-b-2 border-indigo-200">
                        <th className="p-2 text-center font-bold text-indigo-800">
                          <div className="flex flex-col items-center gap-1">
                            <input
                              type="checkbox"
                              className="w-5 h-5 accent-indigo-600"
                              checked={
                                filteredOrders.length > 0 &&
                                filteredOrders.every((o) => selectedOrderNumbers.has(o.orderNumber))
                              }
                              onChange={(e) => setSelectAllVisible(e.target.checked, filteredOrders)}
                            />
                            <span className="text-xs">الطلب</span>
                          </div>
                        </th>
                        <th className="p-4 text-right font-bold text-indigo-800">العميل</th>
                        <th className="p-4 text-right font-bold text-indigo-800">العنوان</th>
                        <th className="p-4 text-center font-bold text-indigo-800">الإجمالي</th>
                        <th className="p-4 text-right font-bold text-indigo-800">المنتجات + الرابط</th>
                        <th className="p-4 text-right font-bold text-indigo-800">ملاحظات</th>
                        <th className="p-4 text-center font-bold text-indigo-800">الإنتاج</th>
                        <th className="p-4 text-center font-bold text-indigo-800">إجراءات</th>
                        <th className="p-4 text-center font-bold text-indigo-800">حذف</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredOrders.map((order) => (
                        <tr key={order.orderNumber} className={namedOrderRowClass(order)}>
                          <td className="p-2 text-center">
                            <div className="flex flex-col items-center gap-1">
                              <input
                                type="checkbox"
                                className="w-5 h-5 accent-indigo-600"
                                checked={selectedOrderNumbers.has(order.orderNumber)}
                                onChange={() => toggleSelectOrder(order.orderNumber)}
                              />
                              <span className="font-mono font-bold text-indigo-600">#{order.orderNumber}</span>
                              <OrderNameMark order={order} />
                            </div>
                          </td>

                          <td className="p-4">
                            <div className="flex items-center gap-2">
                              <User size={16} className="text-gray-500" />
                              <span style={{ fontFamily: "cairo black" }} className="text-lg">
                                {order.name}
                              </span>
                            </div>
                            <br />
                            <div className="space-y-1">
                              <ViewConversationButton
                                order={order}
                                phone={order.phoneNumber}
                                templatePreset="search"
                                canSend={canAct}
                                compact
                                className="w-full"
                              />
                              <button
                                type="button"
                                onClick={() => handleCopyPhoneNumber(order.phoneNumber)}
                                className="flex items-center gap-1 text-green-600 hover:text-green-800 transition cursor-pointer"
                                title="نسخ الرقم"
                              >
                                <Phone size={14} />
                                {order.phoneNumber}
                              </button>
                              {order.phoneNumber2 && order.phoneNumber2 !== "غير معروف" && order.phoneNumber2 !== "Unknown Phone" && (
                                <>
                                  <ViewConversationButton
                                    order={order}
                                    phone={order.phoneNumber2}
                                    templatePreset="search"
                                    canSend={canAct}
                                    compact
                                    className="w-full"
                                    label="عرض محادثة الرقم التاني"
                                  />
                                  <button
                                    type="button"
                                    onClick={() => handleCopyPhoneNumber(order.phoneNumber2)}
                                    className="flex items-center gap-1 text-green-600 hover:text-green-800 text-sm transition cursor-pointer"
                                    title="نسخ الرقم"
                                  >
                                    <Phone size={12} />
                                    {order.phoneNumber2}
                                  </button>
                                </>
                              )}
                            </div>
                          </td>

                          <td className="p-4 max-w-[200px]">
                            <div className="flex items-start gap-2">
                              <MapPin size={16} className="text-gray-500 mt-1 flex-shrink-0" />
                              <span style={{ fontFamily: "cairo black" }} className="text-[13px] line-clamp-5">
                                {order.address}
                              </span>
                            </div>
                            <br />
                            <div className="flex items-center gap-2">
                              <Map size={16} className="text-gray-500" />
                              <span className="text-2xl text-purple-600">{order.state}</span>
                            </div>
                          </td>

                          <td className="p-4 text-center">
                            <span className="font-bold text-xl text-green-600">{order.total} ج.م</span>
                          </td>

                          <td className="p-4">
                            {order.items.map((item, index) => (
                              <div key={`${item.id}-${index}`} className={namedItemBoxClass(item)}>
                                <div className="flex items-start gap-2">
                                  <div className="flex-1">
                                    <div className="font-medium text-indigo-700">{item.name}</div>
                                    <NamedPieceBadge item={item} className="mt-1" />
                                    <div style={{ fontFamily: "cairo black" }} className="text-[13px] text-black-600">
                                      {item.type}
                                    </div>
                                    <div className="flex items-center gap-2 mt-1">
                                      <span className="text-xs bg-gray-100 px-2 py-1 rounded font-mono">{item.code}</span>
                                      {item.driveLink && (
                                        <div className="flex items-center gap-1">
                                          <a
                                            href={item.driveLink}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="text-indigo-600 hover:text-indigo-800 transition-transform hover:scale-110"
                                            title="فتح الرابط"
                                          >
                                            <LinkIcon size={16} />
                                          </a>
                                          {item.code.startsWith("CUSTOM") && (
                                            <button
                                              onClick={() =>
                                                handleDownload(item.driveLink, `${order.orderNumber}-${item.id}.jpg`)
                                              }
                                              className="text-green-600 hover:text-green-800 transition-transform hover:scale-110"
                                              title="تحميل الصورة"
                                            >
                                              <Download size={16} />
                                            </button>
                                          )}
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                </div>
                                {index < order.items.length - 1 && <hr className="my-2 border-gray-200" />}
                              </div>
                            ))}
                          </td>

                          <td className="p-2 max-w-[150px]">
                            <p style={{ fontFamily: "cairo black" }} className="text-lg text-gray-600 line-clamp-3">
                              {order.note || "—"}
                            </p>
                            <OrderChangeImagesButton orderId={order.orderNumber} count={order.changeNoteImageCount} />
                          </td>

                          <td className="p-4">
                            <div className="flex flex-col gap-2 min-w-[100px]">
                              {isAdmin(authState.role) && (
                                <><button
                                  onClick={() => handleCopyAllExcelData(order)}
                                  className="w-full bg-gradient-to-r from-green-500 to-emerald-500 text-white px-3 py-4 rounded-xl hover:shadow-lg hover:scale-105 transition-all duration-300 text-sm font-bold flex items-center justify-center gap-2"
                                >
                                  <Copy size={16} />
                                  نسخ (TeePlus)
                                </button>                                  <button
                                  onClick={() => handleUpdateStatus(order.orderNumber, "Printlet")}
                                  className="bg-fuchsia-600 text-white px-3 py-2 rounded-lg hover:bg-fuchsia-700 text-sm font-medium transition-all"
                                >
                                    <div className="flex items-center justify-center gap-1">
                                      <Send size={14} />
                                      Printlet
                                    </div>
                                  </button>
                                  <button
                                onClick={() => handleUpdateStatus(order.orderNumber, "Girabatcom")}
                                className="bg-cyan-500 text-white px-3 py-2 rounded-lg hover:bg-cyan-600 text-sm font-medium transition-all"
                              >
                                <div className="flex items-center justify-center gap-1">
                                  <Globe size={14} />
                                  Girabatcom
                                </div>
                              </button>
                              
                              <button
                                onClick={() => handleUpdateStatus(order.orderNumber, "Printing Hold")}
                                className="bg-slate-700 text-white px-3 py-2 rounded-lg hover:bg-slate-800 text-sm font-medium transition-all"
                              >
                                <div className="flex items-center justify-center gap-1">
                                  <Printer size={14} />
                                  Printing Hold
                                </div>
                              </button>
                              <button
                                onClick={() => handleUpdateStatus(order.orderNumber, "TeePlus")}
                                className="bg-emerald-600 text-white px-3 py-2 rounded-lg hover:bg-emerald-700 text-sm font-medium transition-all"
                              >
                                <div className="flex items-center justify-center gap-1">
                                  <Shirt size={14} />
                                  Tee Plus
                                </div>
                              </button>
                                  </>
                              )}
                              
                            </div>
                          </td>

                          <td className="p-4">
                            {canAct ? (
                            <div className="flex flex-col gap-2 min-w-[100px]">
                              <button
                                onClick={() => handleUpdateStatus(order.orderNumber, "Hold")}
                                className="bg-yellow-500 text-white px-3 py-2 rounded-lg hover:bg-yellow-600 text-sm font-medium transition-all"
                              >
                                <div className="flex items-center justify-center gap-1">
                                  <Clock size={14} />
                                  معلق
                                </div>
                              </button>
                              <button
                                onClick={() => handleUpdateStatus(order.orderNumber, "Changes")}
                                className="bg-purple-500 text-white px-3 py-2 rounded-lg hover:bg-purple-600 text-sm font-medium transition-all"
                              >
                                <div className="flex items-center justify-center gap-1">
                                  <Edit size={14} />
                                  تغييرات
                                </div>
                              </button>
                              {/* <button
                                onClick={() => handleUpdateStatus(order.orderNumber, "Under Purchase")}
                                className="bg-indigo-500 text-white px-3 py-2 rounded-lg hover:bg-indigo-600 text-sm font-medium transition-all"
                              >
                                <div className="flex items-center justify-center gap-1">
                                  <ShoppingCart size={14} />
                                  تحت الشراء
                                </div>
                              </button> */}
                              <button
                                onClick={() => handleUpdateStatus(order.orderNumber, "Deposit Required")}
                                className="bg-orange-500 text-white px-3 py-2 rounded-lg hover:bg-orange-600 text-sm font-medium transition-all"
                              >
                                <div className="flex items-center justify-center gap-1">
                                  <DollarSign size={14} />
                                  ديبوزت
                                </div>
                              </button>
                              {canAct && (
                              <button
                                onClick={() => handleUpdateStatus(order.orderNumber, "Old")}
                                className="bg-gray-600 text-white px-3 py-2 rounded-lg hover:bg-gray-700 text-sm font-medium transition-all"
                              >
                                <div className="flex items-center justify-center gap-1">
                                  <Archive size={14} />
                                  قديم
                                </div>
                              </button>
                              )}
                              {/* <button
                                onClick={() => handleUpdateStatus(order.orderNumber, "Incomplete")}
                                className="bg-amber-600 text-white px-3 py-2 rounded-lg hover:bg-amber-700 text-sm font-medium transition-all"
                              >
                                <div className="flex items-center justify-center gap-1">
                                  <AlertTriangle size={14} />
                                  Incomplete
                                </div>
                              </button> */}
                            </div>
                            ) : (
                              <p className="text-center text-sm text-slate-500">عرض فقط</p>
                            )}
                          </td>

                          <td className="p-4">
                            {canAct ? (
                            <div className="flex flex-col gap-2 min-w-[80px]">
                              <button
                                onClick={() => handleDeleteOrder(order.orderNumber)}
                                className="bg-red-500 text-white px-3 py-2 rounded-lg hover:bg-red-600 text-sm font-medium transition-all"
                              >
                                <div className="flex items-center justify-center gap-1">
                                  <Trash2 size={14} />
                                  حذف
                                </div>
                              </button>
                              <button
                                onClick={() => handleUpdateStatus(order.orderNumber, "Spam")}
                                className="bg-red-500 text-white px-3 py-2 rounded-lg hover:bg-red-600 text-sm font-medium transition-all"
                              >
                                <div className="flex items-center justify-center gap-1">
                                  <AlertTriangle size={14} />
                                  Spam
                                </div>
                              </button>
                            </div>
                            ) : (
                              <p className="text-center text-sm text-slate-500">عرض فقط</p>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>

                  {hasMore && (
                    <div className="text-center mt-8">
                      <button
                        onClick={loadMoreOrders}
                        disabled={isLoadingMore}
                        className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white px-8 py-4 rounded-xl hover:shadow-xl hover:scale-105 transition-all duration-300 font-bold text-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 mx-auto"
                      >
                        {isLoadingMore ? (
                          <>
                            <Loader className="animate-spin" size={20} />
                            جاري التحميل...
                          </>
                        ) : (
                          <>
                            <ChevronLeft size={20} />
                            تحميل المزيد
                          </>
                        )}
                      </button>
                    </div>
                  )}
                </>
              ) : (
                <div className="text-center py-16">
                  <Package className="mx-auto mb-4 text-gray-400" size={64} />
                  <p className="text-2xl text-gray-700 mb-2">لا توجد طلبات مؤكدة</p>
                  <p className="text-lg text-gray-500">سيتم عرض الطلبات المؤكدة هنا</p>
                </div>
              )}
            </>
          )}
        </div>

        <div className="fixed bottom-6 right-6">
          <button
            onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
            className="bg-gradient-to-r from-blue-600 to-cyan-600 text-white p-4 rounded-full shadow-2xl hover:shadow-xl hover:scale-110 transition-all duration-300 flex items-center justify-center"
            title="العودة إلى الأعلى"
          >
            <ChevronUp size={24} />
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConfirmedOrdersPage;
